import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaNetworkWired, FaServer, FaShieldAlt, FaCheckCircle, FaArrowRight, FaPhone, FaPlug, FaCogs } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const NetworkCablingPage = () => {
    const services = [
        {
            icon: <FaNetworkWired size={24} />,
            title: 'Structured Cabling',
            description: 'Complete Cat5e, Cat6, and Cat6a cabling installations with proper cable management and testing.',
            gradient: 'from-blue-600 to-cyan-500'
        },
        {
            icon: <FaServer size={24} />,
            title: 'Fiber Optic Solutions',
            description: 'High-speed fiber optic installations for backbone connectivity and long-distance runs.',
            gradient: 'from-purple-600 to-indigo-600'
        },
        {
            icon: <FaPlug size={24} />,
            title: 'Patch Panel Installation',
            description: 'Professional patch panel termination and organization for clean network closets.',
            gradient: 'from-teal-500 to-green-500'
        },
        {
            icon: <FaShieldAlt size={24} />,
            title: 'Cable Testing & Certification',
            description: 'Comprehensive testing and certification to ensure optimal network performance.',
            gradient: 'from-orange-500 to-red-500'
        },
        {
            icon: <FaCogs size={24} />,
            title: 'Cable Management',
            description: 'Organized cable routing with proper labeling and documentation for easy maintenance.',
            gradient: 'from-indigo-600 to-blue-600'
        },
        {
            icon: <FaNetworkWired size={24} />,
            title: 'Network Upgrades',
            description: 'Upgrade existing infrastructure to support higher bandwidth and modern requirements.',
            gradient: 'from-rose-500 to-pink-500'
        }
    ];

    const benefits = [
        'Industry-certified technicians',
        'Lifetime warranty on installations',
        'Minimal business disruption',
        'Future-proof designs',
        'Complete documentation',
        'Post-installation support'
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-indigo-400">Network & Cabling</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Professional <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">Network & Cabling</span> Solutions
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Build the backbone of your business with our expert structured cabling and network infrastructure services.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300">
                                    Get Free Quote
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-indigo-600/20 to-purple-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&h=400&fit=crop"
                                    alt="Network Cabling"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Complete Cabling Solutions</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            From design to installation, we handle all aspects of your network cabling needs
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-indigo-200"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-indigo-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=500&fit=crop"
                                alt="Cable Installation"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-4">Why Choose Us</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">The NextGen Advantage</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                Our certified technicians deliver industry-leading cabling installations that exceed standards and stand the test of time.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FaCheckCircle className="text-indigo-600 flex-shrink-0" size={20} />
                                        <span className="text-gray-700 font-medium">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-indigo-600 to-purple-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Upgrade Your Network?</h2>
                    <p className="text-xl text-indigo-100 mb-8">
                        Get a free site survey and customized quote for your cabling project.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-indigo-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Schedule Site Survey
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Speak to an Expert
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default NetworkCablingPage;
